<template>
	<div>
		<div v-if="!isLoading">
			<div v-if="imagesSaved">
				<div class="grid md:grid-cols-2 xxl:grid-cols-3 gap-8 main">
					<MetaInfoImage
						:meta-info-media="repairProof.frontViewImage"
						v-model="repairProof.frontViewImage.isSelected"
						:chooseable="!!isUserAllowedInLevel5or6"
						:chooseable-default="true"
						:deletable="false"
						@input="checkIsFileSelected"
					></MetaInfoImage>
					<MetaInfoImage
						:meta-info-media="repairProof.rearViewImage"
						v-model="repairProof.rearViewImage.isSelected"
						:chooseable="!!isUserAllowedInLevel5or6"
						:chooseable-default="true"
						:deletable="false"
						@input="checkIsFileSelected"
					></MetaInfoImage>
					<MetaInfoImage
						:meta-info-media="repairProof.speedometerImage"
						v-model="repairProof.speedometerImage.isSelected"
						:chooseable="!!isUserAllowedInLevel5or6"
						:chooseable-default="true"
						:deletable="false"
						@input="checkIsFileSelected"
					></MetaInfoImage>
					<MetaInfoImage
						:meta-info-media="repairProof.leftSideViewImage"
						v-model="repairProof.leftSideViewImage.isSelected"
						:chooseable="!!isUserAllowedInLevel5or6"
						:chooseable-default="true"
						:deletable="false"
						@input="checkIsFileSelected"
					></MetaInfoImage>
					<MetaInfoImage
						:meta-info-media="repairProof.rightSideViewImage"
						:chooseable="!!isUserAllowedInLevel5or6"
						:chooseable-default="true"
						v-model="repairProof.rightSideViewImage.isSelected"
						:deletable="false"
						@input="checkIsFileSelected"
					></MetaInfoImage>
					<MetaInfoImage
						v-for="(damageImage, index) in this.repairProof.optionalDamageImages"
						:key="index"
						:meta-info-media="damageImage"
						v-model="damageImage.isSelected"
						:chooseable-default="true"
						:chooseable="!!isUserAllowedInLevel5or6"
						:deletable="false"
						@input="checkIsFileSelected"
					></MetaInfoImage>
				</div>
				<div class="mt-4 flex text-right justify-end" v-if="!isUserAllowedInLevel5or6">
					<div class="pl-6 self-end w-1/3 justify-end">
						<BaseButton class="w-full text-center " @click="getDownloadUrl">{{ $t('download_all_photos') }}</BaseButton>
					</div>
				</div>
				<div v-if="isUserAllowedInLevel5or6" class="w-full mt-6 justify-end text-right">
					<div class="flex justify-end">
						<BaseCheckbox class="w-1/3 pl-6" label="Datum & Uhrzeit in PDF" v-model="printTime" />
					</div>
					<div class="flex flex-row flex-grow justify-end ">
						<div class="w-1/3 flex pl-6 gap-4">
							<BaseButton :disabled="!enableGeneratePdf" @click="createPDF" class="w-2/3">
								{{ $t('generate_pdf') }}
							</BaseButton>
							<BaseButton :disabled="!pdfDownloadURL" @click="downloadFile" class="w-1/3">
								<font-awesome-icon icon="download" style="font-size: 20px"></font-awesome-icon>
							</BaseButton>
						</div>
					</div>
				</div>
			</div>

			<div v-else>
				<div class="main grid md:grid-cols-3 xxl:grid-cols-3 gap-8" v-if="isUserAllowedInLevel5or6 && isAppContainer">
					<RepairProofCamera
						:get-authenticated-user-group-level="getAuthenticatedUserGroupLevel"
						:repair-proof="repairProof.frontViewImage"
						@snapshotCam="snapshotCam"
					/>
					<RepairProofCamera
						:get-authenticated-user-group-level="getAuthenticatedUserGroupLevel"
						:repair-proof="repairProof.rearViewImage"
						@snapshotCam="snapshotCam"
					/>
					<RepairProofCamera
						:get-authenticated-user-group-level="getAuthenticatedUserGroupLevel"
						:repair-proof="repairProof.speedometerImage"
						@snapshotCam="snapshotCam"
					/>
					<RepairProofCamera
						:get-authenticated-user-group-level="getAuthenticatedUserGroupLevel"
						:repair-proof="repairProof.leftSideViewImage"
						@snapshotCam="snapshotCam"
					/>
					<RepairProofCamera
						:get-authenticated-user-group-level="getAuthenticatedUserGroupLevel"
						:repair-proof="repairProof.rightSideViewImage"
						@snapshotCam="snapshotCam"
					/>
					<RepairProofCamera
						v-for="(damageImage, index) in this.repairProof.optionalDamageImages"
						:key="index"
						:get-authenticated-user-group-level="getAuthenticatedUserGroupLevel"
						:repair-proof="damageImage"
						:is-optional-damage-image="true"
						@snapshotCam="snapshotCam"
					/>
					<RepairProofCamera
						:get-authenticated-user-group-level="getAuthenticatedUserGroupLevel"
						:repair-proof="optionalDamageImage"
						:is-optional-damage-image="true"
						@snapshotCam="snapshotCam"
					/>
				</div>
				<div class="mb-10" v-else>
					{{ $t('repair_proofs_only_on_tablet') }}
				</div>
				<div v-if="getAuthenticatedUserGroupLevel !== '2' && isAppContainer" class="flex justify-end mt-4">
					<SaveButton @click="submit()">{{ $t('general.save') }}</SaveButton>
				</div>
			</div>
		</div>
		<LoadingSpinnerWidget v-else />
	</div>
</template>

<script>
import { DamagecasesApiFactory, PdfApiFactory, PDFTypes } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import MetaInfoImage from '@/components/damagecases/forms/MetaInfoImage';
import BaseButton from '@/components/forms/buttons/BaseButton';
import RepairProofCamera from '@/components/damagecases/edit/RepairProofCamera';
import Authentication from '@/mixins/Authentication';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import ProcessLog from '@/mixins/damagecase/ProcessLog';
import SaveButton from '@/components/forms/buttons/SaveButton';
import { default as LoadingSpinnerWidget } from '@/components/navigation/LoadingSpinner.vue';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox.vue';
import { getRegularDate } from '@/plugins/Utils';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';
import AppContainerMixin from '@/mixins/AppContainerMixin';

export default {
	name: 'RepairProof',
	components: { BaseCheckbox, LoadingSpinnerWidget, SaveButton, RepairProofCamera, BaseButton, MetaInfoImage },
	mixins: [Authentication, LoadingSpinner, ProcessLog, AppContainerMixin],
	data() {
		return {
			isLoading: true,
			activateSaveButton: false,
			imagesSaved: false,
			printTime: false,
			pdfDownloadURL: '',
			documentName: '',
			enableGeneratePdf: true,
			repairProof: {
				speedometerImage: {
					title: this.$t('km_number_speedometer'),
					iconPath: require('../../../assets/Tacho.svg'),
					base64Content: null,
				},
				frontViewImage: {
					title: this.$t('front'),
					iconPath: require('../../../assets/Auto-vorne.svg'),
					base64Content: null,
				},
				rearViewImage: {
					title: this.$t('back'),
					iconPath: require('../../../assets/Auto-hinten.svg'),
					base64Content: null,
				},
				leftSideViewImage: {
					title: this.$t('left'),
					iconPath: require('../../../assets/Auto-links.svg'),
					base64Content: null,
				},
				rightSideViewImage: {
					title: this.$t('right'),
					iconPath: require('../../../assets/Auto-rechts.svg'),
					base64Content: null,
				},
				optionalDamageImages: [],
			},
			optionalDamageImage: {
				title: this.$t('originally_damage_area'),
				iconPath: require('../../../assets/Auffahrunfall.svg'),
				base64Content: null,
			},
			cameraIndex: 0,
			cameraEnum: {
				kilometrage: 0,
				left: 1,
				rear: 2,
				right: 3,
				front: 4,
			},
			damageCaseApiFactory: DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios),
		};
	},
	methods: {
		snapshotCam($event) {
			if ($event.isOptional) {
				const tmpData = {
					title: this.optionalDamageImage.title,
					iconPath: this.optionalDamageImage.iconPath,
					base64Content: this.optionalDamageImage.base64Content,
				};
				this.repairProof.optionalDamageImages.push(tmpData);
				this.optionalDamageImage.base64Content = null;
			}
			this.activateSaveButton = this.checkIfAllViewsFilled();
		},
		submit() {
			if (!this.isSaveEnabled) {
				this.$swal(this.$t('general.attention').toString(), this.$t('upload_required_photos').toString(), 'warning');
				return;
			}
			this.setLoadingStatus(true);
			this.damageCaseApiFactory
				.damagecasesIdRepairproofPost(parseInt(this.$route.params.processId || this.$route.params.id), this.repairProof)
				.then(result => {
					this.reportProcessChange(this.$route.params.processId || this.$route.params.id);
					this.repairProof = result.data;
					this.imagesSaved = true;
					this.$store.commit('setConfirmLeaving', false);
					this.$swal(this.$t('general.success').toString(), this.$t('photos_uploaded').toString(), 'success').then(() => {
						this.$router.go(-1);
					});
					this.setLoadingStatus(false);
				})
				.catch(error => {
					this.setLoadingStatus(false);
				});
		},
		checkIfAllViewsFilled() {
			return (
				this.repairProof.speedometerImage.base64Content !== null &&
				this.repairProof.leftSideViewImage.base64Content !== null &&
				this.repairProof.rearViewImage.base64Content !== null &&
				this.repairProof.rightSideViewImage.base64Content !== null &&
				this.repairProof.frontViewImage.base64Content !== null
			);
		},
		checkIsFileSelected() {
			let isSelected = false;
			for (const key in this.repairProof) {
				if (Array.isArray(this.repairProof[key])) {
					const array = this.repairProof[key];
					for (let i = 0; i < array.length; i++) {
						const file = array[i];
						if (file.id && file.isSelected) {
							isSelected = true;
						}
					}
				} else {
					if (this.repairProof[key].id && this.repairProof[key].isSelected) {
						isSelected = true;
					}
				}
			}

			this.enableGeneratePdf = isSelected;
		},
		getDownloadUrl() {
			let url = '';
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdRepairproofDownloadGet(parseInt(this.$route.params.processId || this.$route.params.id))
				.then(response => {
					const fileName = response.headers['content-disposition'].split('filename=')[1];
					const blob = new Blob([response.data]);
					url = window.URL.createObjectURL(blob);
					setTimeout(function() {
						const a = document.createElement('a');
						a.style.display = 'none';
						a.href = url;
						a.download = fileName;
						document.body.appendChild(a);
						a.click();
						window.URL.revokeObjectURL(url);
					}, 200);
				});
		},
		createPDF: function() {
			this.setLoadingStatus(true);
			this.enableGeneratePdf = false;
			this.pdfDownloadURL = '';

			const ids = [];

			for (const key in this.repairProof) {
				if (Array.isArray(this.repairProof[key])) {
					const array = this.repairProof[key];
					for (let i = 0; i < array.length; i++) {
						const file = array[i];

						if (file.id && file.isSelected) {
							ids.push(file.id);
						}
					}
				} else {
					if (this.repairProof[key].id && this.repairProof[key].isSelected) {
						ids.push(this.repairProof[key].id);
					}
				}
			}

			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdGet(parseInt(this.$route.params.processId || this.$route.params.id))
				.then(response => {
					response.data.imageDocumentData = { documentTitle: this.$t('repair_proof_images'), printTime: this.printTime, mediaIds: ids };
					PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.pdfDamagecasesTypeSavePost(PDFTypes.RepairProof, response.data)
						.then(res => {
							this.latestUuid = res.data.uuid;

							this.enableGeneratePdf = true;
							this.$swal(this.$t('general.success').toString(), this.$t('pdf_generated').toString(), 'success');
							new PDFDamagecasesRepository().getSavedDocument(this.latestUuid).then(response => {
								this.documentName = response.headers['content-disposition'].split('filename=')[1];
								this.pdfDownloadURL = new Blob([response.data], { type: 'application/pdf' });
							});
							this.setLoadingStatus(false);
						})
						.catch(error => {
							this.$swal(this.$t('general.error').toString(), this.$t('error_generating_document').toString(), 'error');
							this.enableGeneratePdf = true;
							this.setLoadingStatus(false);
						});
				})
				.catch(error => {
					this.setLoadingStatus(false);
				});
		},
		downloadFile() {
			if (this.isAppContainer) {
				const reader = new FileReader();
				const name = this.documentName;
				reader.onload = function() {
					const arrayBuffer = this.result;
					// convert the ArrayBuffer to a comma-separated string
					const dataString = new Uint8Array(arrayBuffer).join(',');
					const messageData = {
						fileName: name,
						data: dataString,
					};
					// send the data to Swift
					window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
				};
				reader.readAsArrayBuffer(this.pdfDownloadURL);
			} else {
				const url = window.URL.createObjectURL(this.pdfDownloadURL);
				const name = this.documentName;
				setTimeout(function() {
					const a = document.createElement('a');
					a.style.display = 'none';
					a.href = url;
					a.download = name;
					document.body.appendChild(a);
					a.click();
					window.URL.revokeObjectURL(url);
				}, 200);
			}
		},
	},
	computed: {
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		isSaveEnabled() {
			return this.activateSaveButton;
		},
	},
	mounted() {
		this.isLoading = true;
		if (!this.imagesSaved && this.isAppContainer) {
			this.$store.commit('setConfirmLeaving', this.$t('photos_not_saved_leave'));
		}

		this.damageCaseApiFactory
			.damagecasesIdRepairproofGet(parseInt(this.$route.params.processId || this.$route.params.id))
			.then(result => {
				if (result) {
					this.repairProof = result.data;
					this.imagesSaved = this.checkIfAllViewsFilled();
					if (this.imagesSaved) {
						this.$store.commit('setConfirmLeaving', false);
						for (const key in this.repairProof) {
							if (Array.isArray(this.repairProof[key])) {
								const array = this.repairProof[key];
								for (let i = 0; i < array.length; i++) {
									const file = array[i];

									if (file.id) {
										file.isSelected = true;
									}
								}
							} else {
								if (this.repairProof[key].id) {
									this.repairProof[key].isSelected = true;
								}
							}
						}
					}
				}
				this.isLoading = false;
			})
			.catch(error => {
				this.isLoading = false;
			});
	},
};
</script>
